import type { ReactNode } from 'react'

import { useBrandEntitlements } from '@hapana/shared/web/hooks/entitlements.hooks'

import { useIsFeatureFlagEnabled, FeatureFlagKeys } from '_hooks/useFeatureFlag'
import { useEntitlementsSignal } from '_hooks/use-entitlements-signal'
import { EntitlementsContext } from '_contexts/entitlements.context'

interface EntitlementsProviderProps {
  children: ReactNode
}

/**
 * Resolves the BRAND's entitlements once, high in the tree, so every gated route or component
 * reads from one query instead of mounting its own. Brand-scoped on purpose: corporate surfaces
 * (`/c/*`) have no location in context, and Content Suite / Audit History / Reports gate on the
 * brand's plan tier, not any one location's. Site-scoped features (e.g. per-location retail) read
 * their own site entitlements at the point of use.
 *
 * Fails open — while the query loads or if tiering is off for the brand, every feature reads as
 * entitled, so nothing changes until a brand is deliberately switched on. The provider does not block
 * the whole app on the fetch; brand-scoped gated surfaces fold `entitlements.isLoading` into their own
 * loading state, so the app paints immediately and only a gated control waits.
 */
export const EntitlementsProvider = ({ children }: EntitlementsProviderProps) => {
  // Read the SAME flag the backend gates on (`shouldGateFeatures`), fail-closed like its
  // `isFeatureFlagEnabled`. This is the FE chokepoint mirroring the backend's `if (!shouldGateFeatures)
  // …`: only a confirmed-on flag fetches entitlements and enforces gating; off/loading skips the fetch
  // entirely, so gating is inert (fail-open) with no network call — exactly the backend's off path.
  const tieringOn = useIsFeatureFlagEnabled(FeatureFlagKeys.ProductTiering)
  // No corporateId: Core resolves the brand from its own subdomain, so the cache key stays global to
  // this app. The flag is Core's fetch kill-switch (off ⇒ no fetch ⇒ fail-open).
  const entitlements = useBrandEntitlements(undefined, tieringOn)
  // Re-read entitlements the moment the platform changes this brand's tier or catalog.
  useEntitlementsSignal(tieringOn)

  return (
    <EntitlementsContext.Provider value={entitlements}>{children}</EntitlementsContext.Provider>
  )
}
